import { useState } from 'react';
import { useKV } from '@/hooks/useLocalStorage';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  BookOpen,
  CheckCircle,
  GraduationCap,
  Play,
  Target,
} from '@phosphor-icons/react';
import { Tutorial } from '@/lib/tutorialTypes';

interface TutorialMenuProps {
  tutorials: Tutorial[];
  onTutorialStart: (tutorial: Tutorial) => void;
}

export function TutorialMenu({
  tutorials,
  onTutorialStart,
}: Readonly<TutorialMenuProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [completedTutorials] = useKV<string[]>('completed-tutorials', []);

  const completedCount = completedTutorials.length;
  const totalCount = tutorials.length;
  const progress = (completedCount / totalCount) * 100;

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleTutorialStart = (tutorial: Tutorial) => {
    onTutorialStart(tutorial);
    setIsOpen(false);
  };

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        variant='outline'
        className='gap-2 relative'
      >
        <GraduationCap className='w-4 h-4' />
        <span className='hidden sm:inline'>Tutorials</span>
        {completedCount > 0 && (
          <Badge variant='secondary' className='text-xs ml-1'>
            {completedCount}/{totalCount}
          </Badge>
        )}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className='max-w-2xl max-h-[80vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <GraduationCap className='w-5 h-5' />
              Learning Center
            </DialogTitle>
            <DialogDescription>
              Interactive tutorials to master data science fundamentals
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-6'>
            {/* Progress Overview */}
            {completedCount > 0 && (
              <Card className='border-green-200 bg-green-50/30'>
                <CardHeader className='pb-3'>
                  <div className='flex items-center justify-between'>
                    <div>
                      <CardTitle className='text-lg text-green-800'>
                        Your Progress
                      </CardTitle>
                      <CardDescription className='text-green-700'>
                        {completedCount} of {totalCount} tutorials completed
                      </CardDescription>
                    </div>
                    <div className='text-right'>
                      <div className='text-2xl font-bold text-green-800'>
                        {Math.round(progress)}%
                      </div>
                      <div className='text-xs text-green-600'>Complete</div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className='pt-0'>
                  <Progress value={progress} className='h-2' />
                </CardContent>
              </Card>
            )}

            {/* Tutorial List */}
            <div className='space-y-3'>
              {tutorials.map((tutorial, index) => {
                const isCompleted = completedTutorials.includes(tutorial.id);
                const isAccessible =
                  index === 0 ||
                  completedTutorials.includes(tutorials[index - 1]?.id);

                let cardClass = '';

                if (isCompleted) {
                  cardClass = 'border-green-200 bg-green-50/30';
                } else if (isAccessible) {
                  cardClass =
                    'hover:shadow-md cursor-pointer border-blue-200 bg-blue-50/20';
                } else {
                  cardClass = 'opacity-60 bg-muted/20';
                }

                return (
                  <Card
                    key={tutorial.id}
                    className={`transition-all ${cardClass}`}
                  >
                    <CardHeader className='pb-3'>
                      <div className='flex items-start justify-between'>
                        <div className='flex items-start gap-3 flex-1'>
                          {(() => {
                            let iconBgClass = '';

                            if (isCompleted) {
                              iconBgClass = 'bg-green-100 text-green-600';
                            } else if (isAccessible) {
                              iconBgClass = 'bg-primary/10 text-primary';
                            } else {
                              iconBgClass = 'bg-muted text-muted-foreground';
                            }

                            let iconElement;

                            if (isCompleted) {
                              iconElement = <CheckCircle className='w-5 h-5' />;
                            } else if (isAccessible) {
                              iconElement = tutorial.icon;
                            } else {
                              iconElement = <BookOpen className='w-5 h-5' />;
                            }

                            return (
                              <div
                                className={`p-2 rounded-lg flex-shrink-0 ${iconBgClass}`}
                              >
                                {iconElement}
                              </div>
                            );
                          })()}
                          <div className='flex-1 min-w-0'>
                            <CardTitle className='text-base flex items-center gap-2 mb-1'>
                              {tutorial.title}
                              {isCompleted && (
                                <CheckCircle className='w-4 h-4 text-green-600 flex-shrink-0' />
                              )}
                            </CardTitle>
                            <div className='flex items-center gap-2 mb-2'>
                              <Badge
                                variant='outline'
                                className={`text-xs ${getDifficultyColor(tutorial.difficulty)}`}
                              >
                                {tutorial.difficulty}
                              </Badge>
                              <span className='text-xs text-muted-foreground'>
                                {tutorial.estimatedTime}
                              </span>
                              <span className='text-xs text-muted-foreground'>
                                • {tutorial.steps.length} steps
                              </span>
                            </div>
                            <CardDescription className='text-sm line-clamp-2'>
                              {tutorial.description}
                            </CardDescription>
                          </div>
                        </div>
                        <div className='flex-shrink-0 ml-3'>
                          {isAccessible ? (
                            <Button
                              onClick={() => handleTutorialStart(tutorial)}
                              size='sm'
                              className='gap-1'
                              variant={isCompleted ? 'outline' : 'default'}
                            >
                              <Play className='w-3 h-3' />
                              {isCompleted ? 'Review' : 'Start'}
                            </Button>
                          ) : (
                            <div className='text-center'>
                              <div className='text-xs text-muted-foreground mb-1'>
                                Complete previous
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                tutorial to unlock
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>

            {/* Help Text */}
            <Card className='border-blue-200 bg-blue-50/30'>
              <CardContent className='p-4'>
                <div className='flex items-start gap-3'>
                  <Target className='w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5' />
                  <div>
                    <h4 className='font-medium text-blue-800 mb-1'>
                      Learning Path
                    </h4>
                    <p className='text-sm text-blue-700'>
                      Tutorials unlock progressively as you complete them. Start
                      with "Your First Data Exploration" to build a solid
                      foundation, then advance to more specialized topics.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
